import { Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material";
import FormProvider, { RHFAutocomplete } from "src/components/hook-form";

import Iconify from "src/components/iconify";
import { useBoolean } from "src/hooks/use-boolean";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import GenericInfiniteScrollList from "../../generic-infinite-scroll-list";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { tagAPI, useGetTagsForSearchTermQuery, useGetTagsListQuery, useGetTagsQuery, useTagSearchTermMutation } from "src/context/api";
import { useEffect, useMemo, useRef, useState } from "react";
import { enqueueSnackbar } from "notistack";

const schema = yup.object().shape({
    search_term: yup.string().required(),
    tags: yup.array().of(yup.string().required()).required().min(1),
});

// Better hash function for more diverse color generation
export const generateColorFromString = (str: string): string => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
        hash = hash & hash;
    }
    // Use HSL with softer/flat colors - reduced saturation and adjusted lightness
    const hue = Math.abs(hash) % 360;
    const saturation = 40 + (Math.abs(hash >> 8) % 15); // Reduced from 65-85% to 40-55%
    const lightness = 55 + (Math.abs(hash >> 16) % 10); // Adjusted from 50-65% to 55-65%
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

export default function AddTagCell(params: any & { page: number, pageSize: number }) {
    const workspace = useCurrentWorkspace();
    const { data: { tags: tagsList = [] } = {} } = useGetTagsListQuery({ workspaceId: workspace?.id as number }, { skip: !workspace?.id });
    const { data: { tags: tagsForSearchTerm = [] } = {} } = useGetTagsForSearchTermQuery({ workspaceId: workspace?.id as number, searchTerm: params.row.id }, { skip: !workspace?.id || !params.row.id });
    const [tagSearchTerm] = useTagSearchTermMutation();
    const addTagsModal = useBoolean(false);
    const defaultValues = {
        search_term: params.row.id,
        tags: [] as string[],
    };
    const methods = useForm({
        resolver: yupResolver(schema),
        defaultValues,
    });

    const [tagsOptions, setTagsOptions] = useState<any[]>([]);
    const hasFilledTags = useRef(false);
    const hasCurrentTags = useRef(false);

    useEffect(() => {
        setTagsOptions((prev) => [...prev, ...(tagsList?.filter((tag: any) => !tagsOptions.some((t: any) => t.label === tag.label)).map((tag: any) => ({
            id: tag.id,
            label: tag.name,
        }))) || []]);
    }, [tagsList]);

    useEffect(() => {
        if (tagsForSearchTerm?.length !== methods.getValues("tags").length) {
            methods.setValue("tags", tagsForSearchTerm?.map((tag: any) => tag.name) || [] as string[]);
        }
        if (hasCurrentTags.current) return;
        if (tagsForSearchTerm?.length > 0) {
            hasCurrentTags.current = true;
            setTagsOptions((prev) => [...prev, ...(tagsForSearchTerm?.map((tag: any) => ({
                id: tag.id,
                label: tag.name,
            }))) || []]);
            methods.setValue("tags", tagsForSearchTerm?.map((tag: any) => tag.name) || [] as string[]);
        }
    }, [tagsForSearchTerm]);

    const deduplicateTagsOptions = useMemo(() => {
        return Array.from(new Set(tagsOptions.map((tag: any) => tag.label))).map((label: string) => tagsOptions.find((tag: any) => tag.label === label));
    }, [tagsOptions]);

    const onSubmit = (data: any) => {
        tagSearchTerm({ workspaceId: workspace?.id as number, searchTerm: params.row.id, tags: data.tags.map((tag: any) => ({ label: tag })) })
            .unwrap()
            .then(() => {
                addTagsModal.onFalse();
                methods.reset();
                enqueueSnackbar("Searchterm tagged successfully", { variant: "success" });
            })
            .catch((error) => {
                enqueueSnackbar("Searchterm tagging failed", { variant: "error" });
            });
    }

    return (
        <Stack direction="row" px={0} mx={0} justifyContent="flex-end" alignItems="center" gap={1}>
            <Button variant={"contained"} onClick={addTagsModal.onTrue} startIcon={<Iconify icon="solar:add-circle-bold" />} sx={{ px: 1.5, mx: 0, my: 1 }} color="primary">Add tags</Button>
            <Dialog fullWidth maxWidth="sm" open={addTagsModal.value} onClose={addTagsModal.onFalse}>
                <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
                    <DialogTitle>Add tags</DialogTitle>
                    <DialogContent sx={{ p: 2 }}>
                        <TextField label="Searchterm" disabled value={params.row.id} fullWidth sx={{ mt: 2 }} />
                        <GenericInfiniteScrollList name="tags"
                            sx={{ mt: 2 }}
                            searchPlaceholder="Search for a tag"
                            fullWidth={true}
                            multiple={true}
                            freeSolo={true}
                            options={tagsOptions}
                            items={deduplicateTagsOptions}
                            transformValue={(valueId: string) => tagsOptions.find((tag: any) => tag.id === valueId)?.label || valueId as any}
                            customItemRenderer={(option) => (
                                <Chip
                                    label={option.label || option as any}
                                    size="small"
                                    variant="soft"
                                    sx={{
                                        backgroundColor: option.label || option as any
                                            ? generateColorFromString(String(option.label || option))
                                            : undefined,
                                        color: "#fff"
                                    }}
                                />
                            )}
                            customTagRenderer={(option) => (
                                <Chip
                                    label={option.label || option as any}
                                    size="small"
                                    variant="soft"
                                    sx={{
                                        cursor: "pointer",
                                        marginRight: 0.5,
                                        backgroundColor: option.label || option as any
                                            ? generateColorFromString(String(option.label || option))
                                            : undefined,
                                        color: "#fff"
                                    }}
                                    onDelete={() => {
                                        methods.setValue("tags", methods.getValues("tags").filter((tag: any) => tag.id !== option.id));
                                    }}
                                />
                            )}
                            setItems={(items) => setTagsOptions(items)}
                            getOptionLabel={(option) => (option as any).label || option as any}
                            getOptionKey={(option) => (option as any).id || option as any}
                            isOptionEqualToValue={(option, value) => {
                                const isOptionIDEqual = (option.id || option as any) === value;
                                const isOptionLabelEqual = (option.label || option as any) === value;
                                return isOptionIDEqual || isOptionLabelEqual;
                            }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button variant="contained" sx={{backgroundColor: "#181818"}} color="primary" type="submit">Add tags</Button>
                    </DialogActions>
                </FormProvider>
            </Dialog>
        </Stack>
    )
}